import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import moment from "moment";
import { Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";

import { SUBMISSION_SPEC_FLAGS_MAP } from "../../Common/constants/SpecFlagConstants";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import SuccessAnimation from "../../Common/UIComponents/SuccessAnimation";
import { useAccessStatus } from "../../grace-period/hooks/useAccessStatus";
import { EVENT_SCHEDULE_CATEGORY_MAP } from "../Analytics/Components/Constant";
import EnvConfirmModal from "../Analytics/Components/EnvConfirmModal";
import PopoverMenu from "../components/PopoverMenu";
import CalendarReceiptModal from "./CalendarReceiptModal";
import LocationDetails, { isMarkingAllowed } from "./LocationDetails";

import { isResultRevealingSpec } from "../../grace-period/utils";

import { submitSamples } from "../../../actions/envCalendar";

import { ReactComponent as GraceLockIcon } from "../../../assets/images/common/GraceLock.svg";
import { ReactComponent as ContextMenuIcon } from "../../../assets/images/environment/ContextMenuIcon.svg";
import { ReactComponent as EditPencilIcon } from "../../../assets/images/environment/EditPencilIcon.svg";
import { ReactComponent as PrinterIcon } from "../../../assets/images/environment/PrinterIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/images/environment/TrashIcon.svg";
import { ReactComponent as WarningIcon } from "../../../assets/images/environment/WarningIcon.svg";

import "./EventCard.css";

const DefaultSubmitModalObj = { open: false, bodyText: "" };
const MAX_TITLE_LENGTH_FOR_TOOLTIP = 100;

// const scrollbarStyle = {
//   style: {
//     visibility: "hidden",
//     opacity: 0,
//     transition: "visibility 0s 0.3s, opacity 0.3s linear",
//   },
// };

const getVisibleMenuItems = (menuItems) => menuItems.filter((item) => {
  const isVisible = typeof item.isVisible === "function" ? item.isVisible() : item.isVisible;
  return isVisible !== false;
});

export default function EventCard({
  data,
  index,
  setDeleteEventData,
  setIsDeletingConfirm,
  afterMarkingSample,
  onEdit,
  cardLoading,
  setCardLoading,
  onPrintLabel,
  submissionCategory,
  eventsByDate,
  markedDates,
}) {
  const { areResultsLocked } = useAccessStatus();
  const [openPopover, setOpenPopover] = useState(false);
  const [submitModalObj, setSubmitModalObj] = useState(DefaultSubmitModalObj);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const submissionForReceiptModal = useRef();
  const scrollbarRef = useRef();
  const isDeletable = (
    (data.submission_category !== "swabathon" && (data.status === "-1" || data.status === "-2"))
  || (data.submission_category === "swabathon" && data.status === "-2")
  );

  const popoverMenuItems = [
    {
      title: "Edit",
      icon: <EditPencilIcon />,
      isVisible: () => (data.status === "-2" || data.status === "-1") && data.submission_category !== "swabathon",
      onClick: () => {
        setOpenPopover(false);
        onEdit(data);
      },
    },
    {
      title: "Delete",
      icon: <TrashIcon />,
      isVisible: () => isDeletable,
      onClick: () => {
        setOpenPopover(false);
        const { event_id, submission_id, event_frequency } = data;
        setDeleteEventData((state) => ({
          ...state, event_id, submission_id, event_frequency, submission_category: submissionCategory,
        }));
        setIsDeletingConfirm(true);
      },
    },
    {
      title: "Print",
      icon: <PrinterIcon />,
      isVisible: () => !areResultsLocked,
      onClick: () => {
        setOpenPopover(false);
        onPrintLabel(data);
      },
    },
  ];

  const visiblePopoverMenuItems = getVisibleMenuItems(popoverMenuItems);
  const showResultLockedCapsule = areResultsLocked
    && data.samples_list.some((sample) => isResultRevealingSpec(sample.specs_flag));

  const showSubmit = useMemo(() => { // show the submit button if the submission has not been submitted yet and there is at least one sample has been sampled
    const { status, samples_list, event_date } = data;
    const atLeastOneSampled = samples_list.filter((sample) => sample.sampled).length > 0;
    return status === "-2" && atLeastOneSampled && isMarkingAllowed(event_date);
  }, [data]);

  /**
   * Show submit confirmation
   */
  const showSubmitConfirmation = () => {
    const notSampled = data.samples_list.filter((sample) => !sample.sampled).length;
    const bodyText = notSampled === 0 ? `Samples (${data.samples_list.length}) will be submitted for testing.` : (
      <div>
        Not all samples have been sampled.
        <br />
        <br />
        Only samples marked as Sampled will be submitted. All other samples will be saved as Draft.
        <br />
        <br />
        Are you sure want to submit the submission?
      </div>
    );
    setSubmitModalObj({
      open: true,
      bodyText,
    });
  };

  /**
   * Handles sample submit
   */
  const handleSubmitSamples = async () => {
    setSubmitLoading(true);
    const payload = {
      submission_id: data.submission_id,
      action: "Submit",
    };
    const { success, message } = await submitSamples(payload);
    if (success) {
      toast.success(message);
      setSubmitModalObj(DefaultSubmitModalObj);
      // Success animation
      submissionForReceiptModal.current = {
        samples_list: data.samples_list.map((sample) => ({ ...sample, tests_list: sample.test_list })),
        num_samples: data.samples_list.length,
        submit_date: moment(Date.now()).format(),
        submit_name: data.submission_name,
        po: data.po,
        comment: data.comment,
      };
      setShowSuccessAnimation(true);
      await afterMarkingSample();
    } else {
      toast.error(message);
    }
    setSubmitLoading(false);
  };

  useEffect(() => {
    if (showSuccessAnimation) {
      setTimeout(() => {
        setShowSuccessAnimation(false);
        setShowReceiptModal(true);
      }, 1000);
    }
  }, [showSuccessAnimation]);

  /**
   * Handle when submission is successful and user wants to close the receipt modal
   */
  const handleSubmissionDone = () => {
    setShowReceiptModal(false);
  };

  return (
    <>
      <Draggable key={data.submission_id} index={index} draggableId={data.submission_id} isDragDisabled={data.status !== "-2"}>
        {(provided) => (
          <div
            className={`EventGrid__EventCard ${submissionCategory === "randomizer" ? "EventGrid__EventCard--Randomizer" : ""}`}
            ref={provided.innerRef}
            {...provided.draggableProps} // eslint-disable-line
            {...provided.dragHandleProps} // eslint-disable-line
          >
            <div className="EventGrid__EventCardHeader">
              {data.submission_name.length > MAX_TITLE_LENGTH_FOR_TOOLTIP ? (
                <StyledTooltip title={data.submission_name} className="EventGrid__EventCardHeader__Tooltip">
                  <div>
                    {(data.submission_category === "corrective_action") && <WarningIcon />}
                    <p>{data.submission_name}</p>
                  </div>
                </StyledTooltip>
              ) : (
                <div>
                  {(data.submission_category === "corrective_action") && <WarningIcon />}
                  <p>{data.submission_name}</p>
                </div>
              )}
              {visiblePopoverMenuItems.length > 0 && (
                <PopoverMenu menuItems={popoverMenuItems} open={openPopover} setOpen={setOpenPopover}>
                  <button type="button">
                    <ContextMenuIcon />
                  </button>
                </PopoverMenu>
              )}
            </div>
            {/* {data.scheduled_from === "report-add" && <p className="Event__Location">Created Via Reports</p>} */}
            {data.submission_category
              && data.submission_category !== "scheduling_calendar"
              && EVENT_SCHEDULE_CATEGORY_MAP[data.submission_category] && (
                <p className="Event__Location">{EVENT_SCHEDULE_CATEGORY_MAP[data.submission_category].label}</p>
            )}
            {SUBMISSION_SPEC_FLAGS_MAP[data.status] && (
              <p className="Event__Location">{SUBMISSION_SPEC_FLAGS_MAP[data.status].display_name}</p>
            )}
            <StyledScrollbar
              autoHide
              className={`Event_LocationsList__Scrollbar ${submissionCategory === "randomizer" ? "Event_LocationsList__Scrollbar--Randomizer" : ""}`}
              ref={scrollbarRef}
              // trackYProps={scrollbarStyle}
            >
              <div className="Event_LocationsList">
                {data.samples_list.map((sample, i) => (
                  <LocationDetails
                    key={`${sample.sample_id}-${i}`}
                    sampleId={sample.sample_id}
                    specsFlag={sample.specs_flag}
                    submissionId={sample.submission_id}
                    submissionStatus={data.status}
                    sampleType={sample.sample_type}
                    sampled={sample.sampled}
                    afterMarkingSample={afterMarkingSample}
                    draggableId={data.submission_id}
                    setCardLoading={setCardLoading}
                    event_date={data.event_date}
                    submissionCategory={submissionCategory}
                    testList={sample.test_list}
                    currentEvent={data}
                    eventsByDate={eventsByDate}
                    markedDates={markedDates}
                  />
                ))}
                {showResultLockedCapsule && (
                  <div className="Event_ResultLocked">
                    <GraceLockIcon className="Event_ResultLocked__Icon" aria-hidden />
                    <span>Result Locked</span>
                  </div>
                )}
              </div>
            </StyledScrollbar>
            {showSubmit && (
              <Button
                type="text"
                htmlType="button"
                size="small"
                className="EventGrid__EventCard__SubmitBtn"
                onClick={showSubmitConfirmation}
              >
                Submit
              </Button>
            )}
            {submitModalObj.open && (
              <EnvConfirmModal
                width="400px"
                headerText="Submission Confirmation"
                buttonText={["Cancel", "Submit"]}
                loading={submitLoading}
                bodyText={submitModalObj.bodyText}
                buttonFunctions={[() => setSubmitModalObj(DefaultSubmitModalObj),
                  handleSubmitSamples]}
              />
            )}
            {cardLoading.isLoading && cardLoading.draggableId === data.submission_id && (
              <div className="Drag_Loading">
                <LoadingOutlined spin className="Drag_Loading__Loader" />
              </div>
            )}
          </div>
        )}
      </Draggable>
      {showSuccessAnimation && <SuccessAnimation />}
      {showReceiptModal && (
        <CalendarReceiptModal
          maskClosable={false}
          customerId={data.customer_id}
          po={data.po}
          comment={data.comment}
          submissionId={data.submission_id}
          submissionFromParent={submissionForReceiptModal.current}
          showReceipt={showReceiptModal}
          handleReceipt={handleSubmissionDone}
          headerTitleText=""
        // confirmButtonText={isLoggedOut ? "Submit Another Submission" : "Go To Submissions Page"}
        />
      )}
    </>
  );
}
